import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef, useMemo } from "react";
import { toast } from "react-toastify";
import api from "../../Utils/Axios";
import ReceiptView from "../ReceiptView";
import * as XLSX from "xlsx";

const getTodayDate = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const toLocalISOString = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function ReceiptManagement() {
  const navigate = useNavigate();
  const [ledgerParties, setLedgerParties] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [companyLanguage, setCompanyLanguage] = useState('en');
  // New: separate tabs for suppliers and customers
  const [activeTab, setActiveTab] = useState('suppliers'); // 'suppliers' | 'customers'
  const [selectedParty, setSelectedParty] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [company, setCompany] = useState(null);
  const [selectedParties, setSelectedParties] = useState([]); // Array of selected party IDs
  const [multipleReceiptsData, setMultipleReceiptsData] = useState([]); // Array of receipt data for multiple receipts
  const [generatingReceiptId, setGeneratingReceiptId] = useState(null); // Track which receipt is being generated
  const [selectedDate, setSelectedDate] = useState(getTodayDate()); // Date filter for sales/bills - default to today
  const [showAllParties, setShowAllParties] = useState(false); // Toggle to show all parties or only those with transactions on date
  const [viewMode, setViewMode] = useState('sale-wise'); // 'sale-wise' | 'receipt'
  const [partiesWithDateSales, setPartiesWithDateSales] = useState(new Set()); // Set of party IDs that have sales/bills on selected date
  const [partyDateSalesTotals, setPartyDateSalesTotals] = useState({}); // Map of party ID to total sale/bill amount on selected date
  const [isLoadingDateFilter, setIsLoadingDateFilter] = useState(false);
  const [allReceiptsData, setAllReceiptsData] = useState([]); // Data for all receipts in 'receipt' tab
  const [isGeneratingAll, setIsGeneratingAll] = useState(false); // Loading state for 'receipt' tab
  const [isGeneratingSelected, setIsGeneratingSelected] = useState(false); // Loading state for generating multiple receipts

  useEffect(() => {
    const lang = localStorage.getItem("companyLanguage") || 'en';
    setCompanyLanguage(lang);
    fetchParties();
    fetchCompany();
  }, []);

  // Fetch parties with sales/bills on selected date
  useEffect(() => {
    if (selectedDate) {
      // Only fetch if ledgerParties are loaded (for name matching fallback)
      if (ledgerParties.length > 0) {
        fetchPartiesByDate();
      }
    } else {
      // If no date selected, show all parties
      setPartiesWithDateSales(new Set());
    }
  }, [selectedDate, activeTab]);

  // Re-fetch when ledgerParties are loaded and date is selected
  useEffect(() => {
    if (selectedDate && ledgerParties.length > 0) {
      fetchPartiesByDate();
    }
  }, [ledgerParties.length]);

  const getToken = () => {
    return localStorage.getItem("companyToken");
  };



  const fetchCompany = async () => {
    try {
      const token = getToken();
      if (!token) {
        navigate("/company/login");
        return;
      }
      const response = await api.get("/company/profile", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompany(response.data.company);
    } catch (error) {
      console.error("Fetch company error:", error);
    }
  };

  const fetchParties = async () => {
    try {
      setIsLoading(true);
      const token = getToken();
      if (!token) {
        navigate("/company/login");
        return;
      }

      // Fetch all parties with balances
      const response = await api.get('/ledger/parties', {
        headers: { Authorization: `Bearer ${token}` }
      });

      const allParties = response.data.parties || [];
      // Filter parties with non-zero balances (receivable or payable)
      const partiesWithBalance = allParties.filter(party => {
        const balance = party.currentBalance || 0;
        return balance !== 0;
      });

      setLedgerParties(partiesWithBalance);
    } catch (error) {
      console.error('Fetch parties error:', error);
      toast.error(companyLanguage === 'ur' ? 'پارٹیز نہیں مل سکیں' : 'Failed to load parties');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch parties that have sales/bills on the selected date
  const fetchPartiesByDate = async () => {
    if (!selectedDate) {
      setPartiesWithDateSales(new Set());
      setPartyDateSalesTotals({});
      return;
    }

    try {
      setIsLoadingDateFilter(true);
      const token = getToken();
      if (!token) {
        return;
      }

      const partiesWithSalesOnDate = new Set();
      const salesTotalsOnDate = {};

      if (activeTab === 'customers') {
        // Fetch sales for a wider range to handle timezone shifts, then filter locally
        const dateFrom = new Date(selectedDate);
        dateFrom.setDate(dateFrom.getDate() - 1);
        const dateTo = new Date(selectedDate);
        dateTo.setDate(dateTo.getDate() + 1);

        const salesResponse = await api.get(`/sale/company/all?dateFrom=${dateFrom.toISOString().split('T')[0]}&dateTo=${dateTo.toISOString().split('T')[0]}&limit=1000`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const allSales = salesResponse.data?.sales || [];

        // Filter sales by selected date
        const salesOnDate = allSales.filter(sale => {
          if (!sale.date) return false;
          const saleDate = toLocalISOString(sale.date);
          return saleDate === selectedDate;
        });

        // Get unique customer party IDs from sales on this date
        salesOnDate.forEach(sale => {
          let partyId = null;
          if (sale.customerPartyId) {
            partyId = typeof sale.customerPartyId === 'object'
              ? sale.customerPartyId._id
              : sale.customerPartyId;
            if (partyId) {
              partiesWithSalesOnDate.add(partyId);
            }
          }
          // Also match by name if partyId not available
          if (!partyId && (sale.sellerName || sale.sellerNameUrdu)) {
            ledgerParties.forEach(party => {
              if (party.type === 'customer') {
                const sellerName = sale.sellerNameUrdu || sale.sellerName || '';
                const sellerNameEn = sale.sellerName || '';
                const partyName = party.nameUrdu || party.name || '';
                const partyNameEn = party.name || '';

                if ((sellerName && sellerName === partyName) ||
                  (sellerNameEn && sellerNameEn === partyNameEn)) {
                  partiesWithSalesOnDate.add(party._id);
                  partyId = party._id;
                }
              }
            });
          }

          if (partyId) {
            const amount = parseFloat(sale.totalAmount) || parseFloat(sale.total) || 0;
            salesTotalsOnDate[partyId] = (salesTotalsOnDate[partyId] || 0) + amount;
          }
        });
      } else if (activeTab === 'suppliers') {
        const dateFrom = new Date(selectedDate);
        dateFrom.setDate(dateFrom.getDate() - 1);
        const dateTo = new Date(selectedDate);
        dateTo.setDate(dateTo.getDate() + 1);

        let billsResponse;
        try {
          billsResponse = await api.get(`/bill/company/my-bills?dateFrom=${dateFrom.toISOString().split('T')[0]}&dateTo=${dateTo.toISOString().split('T')[0]}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (error) {
          // Fallback to /all endpoint if my-bills fails
          billsResponse = await api.get(`/bill/company/all?dateFrom=${dateFrom.toISOString().split('T')[0]}&dateTo=${dateTo.toISOString().split('T')[0]}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }

        const allBills = billsResponse.data?.bills || [];

        // Filter bills by selected date
        const billsOnDate = allBills.filter(bill => {
          if (!bill.date) return false;
          const billDate = toLocalISOString(bill.date);
          return billDate === selectedDate;
        });

        // Get unique supplier party IDs from bills on this date
        // Note: In bills, customerName/customerPartyId refers to the supplier
        billsOnDate.forEach(bill => {
          let partyId = null;
          if (bill.customerPartyId) {
            partyId = typeof bill.customerPartyId === 'object'
              ? bill.customerPartyId._id
              : bill.customerPartyId;
            if (partyId) {
              partiesWithSalesOnDate.add(partyId);
            }
          }
          // Also match by name if partyId not available
          if (!partyId && (bill.customerName || bill.customerNameUrdu)) {
            ledgerParties.forEach(party => {
              if (party.type === 'supplier') {
                const customerName = bill.customerNameUrdu || bill.customerName || '';
                const customerNameEn = bill.customerName || '';
                const partyName = party.nameUrdu || party.name || '';
                const partyNameEn = party.name || '';

                if ((customerName && customerName === partyName) ||
                  (customerNameEn && customerNameEn === partyNameEn)) {
                  partiesWithSalesOnDate.add(party._id);
                  partyId = party._id;
                }
              }
            });
          }

          if (partyId) {
            const amount = parseFloat(bill.totalAmount) || parseFloat(bill.total) || 0;
            salesTotalsOnDate[partyId] = (salesTotalsOnDate[partyId] || 0) + amount;
          }
        });
      }

      setPartiesWithDateSales(partiesWithSalesOnDate);
      setPartyDateSalesTotals(salesTotalsOnDate);
    } catch (error) {
      console.error('Fetch parties by date error:', error);
      toast.error(companyLanguage === 'ur' ? 'تاریخ کے مطابق ڈیٹا لوڈ کرنے میں ناکام' : 'Failed to load data by date');
    } finally {
      setIsLoadingDateFilter(false);
    }
  };

  const handleGenerateReceipt = async (party) => {
    setGeneratingReceiptId(party._id);
    try {
      const token = getToken();
      if (!token) {
        setGeneratingReceiptId(null);
        return;
      }

      // Fetch party transactions to get details
      const transactionsResponse = await api.get(`/ledger/parties/${party._id}/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const transactions = transactionsResponse.data.transactions || [];
      const balance = party.currentBalance || 0;
      const isReceivable = balance > 0;

      // Get latest transaction with billId or saleId
      let latestTransaction = null;
      let previousBalance = 0;
      let newBillAmount = 0;
      let billNumberText = null;
      let amountPaid = 0; // Amount paid from recovery (for customer receipts)

      const isCustomer = party.type === 'customer';
      let targetDate = selectedDate || getTodayDate();
      const relevantTransactions = transactions
        .filter(tx => {
          const isRelevantType = isCustomer ? (tx.saleId && tx.type === 'debit') : (tx.billId && tx.type === 'debit');
          if (!isRelevantType) return false;

          const txDate = toLocalISOString(tx.date);
          return txDate === targetDate;
        })
        .sort((a, b) => new Date(b.date) - new Date(a.date));

      // Fetch customer's daily sales for tafseeli akhrajat
      let customerDailySales = [];
      if (isCustomer) {
        try {
          // Fetch all sales for this customer
          // Note: In a large system, this should be filtered by customerId on the backend
          const salesResponse = await api.get('/sale/company/all', {
            headers: { Authorization: `Bearer ${token}` }
          });

          const allSales = salesResponse.data?.sales || [];

          // Filter sales by customer (seller) - match by party ID or name
          const customerSales = allSales.filter(sale => {
            const sellerName = sale.sellerNameUrdu || sale.sellerName || '';
            const sellerNameEn = sale.sellerName || '';
            const partyName = party.nameUrdu || party.name || '';
            const partyNameEn = party.name || '';

            // Match by name (Urdu or English)
            return (sellerName && sellerName === partyName) ||
              (sellerNameEn && sellerNameEn === partyNameEn) ||
              (sale.customerPartyId && (
                (typeof sale.customerPartyId === 'object' && sale.customerPartyId._id === party._id) ||
                (typeof sale.customerPartyId === 'string' && sale.customerPartyId === party._id)
              ));
          });

          // Filter sales by the target date (all sales from that day)
          customerDailySales = customerSales.filter(sale => {
            if (!sale.date) return false;
            const saleDate = toLocalISOString(sale.date);
            return saleDate === targetDate;
          });
        } catch (error) {
          console.error('Error fetching customer sales:', error);
        }
      }

      if (relevantTransactions.length > 0) {
        latestTransaction = relevantTransactions[0];

        // For customers: calculate previous balance as balance before last day's sales
        if (isCustomer && customerDailySales.length > 0) {
          // Calculate total of all sales from last day
          const lastDaySalesTotal = customerDailySales.reduce((sum, sale) => {
            return sum + (parseFloat(sale.totalAmount) || parseFloat(sale.total) || 0);
          }, 0);

          // Previous balance = current balance - last day's sales total
          previousBalance = Math.abs(balance) - lastDaySalesTotal;
          if (previousBalance < 0) previousBalance = 0;

          // New bill amount = total of last day's sales
          newBillAmount = lastDaySalesTotal;
        }

        // For customers: fetch sale number and recovery amount (amountPaid), for suppliers: fetch bill number
        if (isCustomer && latestTransaction.saleId) {
          try {
            // Fetch sale to get serialNumber
            const saleResponse = await api.get(`/sale/company/${latestTransaction.saleId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const sale = saleResponse.data.sale;
            if (sale && sale.serialNumber) {
              // Ensure serialNumber is a string
              const saleSerialNumber = String(sale.serialNumber).trim();
              billNumberText = companyLanguage === 'ur' ? `سیل # ${saleSerialNumber}` : `Sale # ${saleSerialNumber}`;
            } else {
              // Fallback
              billNumberText = companyLanguage === 'ur' ? `سیل # ${latestTransaction.saleId.toString().slice(-6)}` : `Sale # ${latestTransaction.saleId.toString().slice(-6)}`;
            }

            // Get amountPaid from recovery transactions (credit transactions) - same as customer khata
            // Find all credit transactions (recovery transactions) for this customer
            const creditTransactions = transactions
              .filter(tx => tx.type === 'credit' && tx.recoveryId)
              .sort((a, b) => new Date(b.date) - new Date(a.date));

            // Sum up recovery amounts from credit transactions (same as customer khata)
            if (creditTransactions.length > 0) {
              amountPaid = creditTransactions.reduce((sum, tx) => sum + (parseFloat(tx.amount) || 0), 0);

              // Try to fetch latest recovery record to get exact recoveryAmount
              if (creditTransactions[0].recoveryId) {
                try {
                  const recoveryResponse = await api.get(`/recovery/company/${creditTransactions[0].recoveryId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  const recovery = recoveryResponse.data.recovery;
                  if (recovery && recovery.recoveryAmount) {
                    // Use latest recovery amount (or sum all if needed)
                    amountPaid = parseFloat(recovery.recoveryAmount || 0);
                  }
                } catch (recoveryError) {
                  console.error('Error fetching recovery:', recoveryError);
                  // Use transaction amount sum as fallback
                }
              }
            }
          } catch (error) {
            console.error('Error fetching sale:', error);
            billNumberText = companyLanguage === 'ur' ? `سیل # ${latestTransaction.saleId.toString().slice(-6)}` : `Sale # ${latestTransaction.saleId.toString().slice(-6)}`;
            amountPaid = 0; // Reset to 0 on error
          }
        } else if (!isCustomer && latestTransaction.billId) {
          try {
            // Fetch bill to get serialNumber
            const billResponse = await api.get(`/bill/company/${latestTransaction.billId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const bill = billResponse.data.bill;
            if (bill && bill.serialNumber) {
              billNumberText = companyLanguage === 'ur' ? `بل # ${bill.serialNumber}` : `Bill # ${bill.serialNumber}`;
            } else {
              // Fallback
              billNumberText = companyLanguage === 'ur' ? `بل # ${latestTransaction.billId.toString().slice(-6)}` : `Bill # ${latestTransaction.billId.toString().slice(-6)}`;
            }
          } catch (error) {
            console.error('Error fetching bill:', error);
            billNumberText = companyLanguage === 'ur' ? `بل # ${latestTransaction.billId.toString().slice(-6)}` : `Bill # ${latestTransaction.billId.toString().slice(-6)}`;
          }
        }

        // For non-customer or if customer daily sales not available, use transaction amount
        if (!isCustomer || customerDailySales.length === 0) {
          // Calculate new bill amount from transaction
          newBillAmount = latestTransaction.amount || 0;

          // Calculate previous balance
          previousBalance = Math.abs(balance) - newBillAmount;
          if (previousBalance < 0) previousBalance = 0;
        }
      } else {
        // No relevant transactions found
        previousBalance = Math.abs(balance);
      }

      // Prepare receipt data
      const receipt = {
        party: party,
        balance: Math.abs(balance),
        isReceivable: isReceivable,
        transactions: transactions,
        date: new Date().toLocaleDateString('en-CA'),
        company: company,
        latestTransaction: latestTransaction,
        billNumber: billNumberText, // "بل # 1" or "سیل # 1" format
        billDate: latestTransaction ? latestTransaction.date : null,
        previousBalance: previousBalance,
        newBillAmount: newBillAmount,
        amountPaid: amountPaid, // Amount paid from sale (for customer receipts)
        totalPayable: Math.abs(balance),
        customerDailySales: customerDailySales // Daily sales for tafseeli akhrajat (customer receipts only)
      };

      setReceiptData(receipt);
      setShowReceipt(true);
    } catch (error) {
      console.error('Generate receipt error:', error);
      toast.error(companyLanguage === 'ur' ? 'رسید بنانے میں ناکام' : 'Failed to generate receipt');
    } finally {
      setGeneratingReceiptId(null);
    }
  };

  const handlePrintReceipt = () => {
    window.print();
  };

  const handlePrintList = () => {
    window.print();
  };

  const handleCloseReceipt = () => {
    setShowReceipt(false);
    setReceiptData(null);
    setMultipleReceiptsData([]);
    setSelectedParties([]);
  };

  const filteredParties = useMemo(() => {
    return ledgerParties
      .filter(party => {
        if (activeTab === 'suppliers') return party.type === 'supplier';
        if (activeTab === 'customers') return party.type === 'customer';
        return true;
      })
      // Filter by date if selected AND not showing all (skip if in receipt mode)
      .filter(party => {
        if (viewMode === 'receipt' || showAllParties || !selectedDate) return true;
        return partiesWithDateSales.has(party._id);
      })
      // Then apply search filter
      .filter(party => {
        if (!searchFilter) return true;
        const searchLower = searchFilter.toLowerCase();
        const name = (party.name || '').toLowerCase();
        const nameUrdu = (party.nameUrdu || '').toLowerCase();
        return name.includes(searchLower) || nameUrdu.includes(searchLower);
      });
  }, [ledgerParties, activeTab, showAllParties, selectedDate, partiesWithDateSales, searchFilter, viewMode]);

  const handleToggleSelection = (partyId) => {
    setSelectedParties(prev => {
      if (prev.includes(partyId)) {
        // Deselect
        return prev.filter(id => id !== partyId);
      } else {
        // Select (unlimited, but will be paginated 4 per page)
        return [...prev, partyId];
      }
    });
  };

  const handleSelectAll = () => {
    const allFilteredIds = filteredParties.map(party => party._id);
    const allSelected = allFilteredIds.every(id => selectedParties.includes(id));

    if (allSelected) {
      // Deselect all filtered parties
      setSelectedParties(prev => prev.filter(id => !allFilteredIds.includes(id)));
    } else {
      // Select all filtered parties
      setSelectedParties(prev => {
        const newSelection = [...prev];
        allFilteredIds.forEach(id => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      });
    }
  };

  const isAllSelected = filteredParties.length > 0 && filteredParties.every(party => selectedParties.includes(party._id));
  const isSomeSelected = filteredParties.some(party => selectedParties.includes(party._id));
  const selectAllCheckboxRef = useRef(null);

  // Generate receipt data for all filtered parties
  const generateAllReceiptsData = async () => {
    try {
      const token = getToken();
      if (!token) return [];

      // Clear previous data
      setAllReceiptsData([]);

      // Split partyIds into chunks of 10 to avoid timeouts
      const CHUNK_SIZE = 10;
      const allPartyIds = filteredParties.map(p => p._id);

      // Limit to first 100 parties to avoid browser crash/memory issues
      const partyIdsToProcess = allPartyIds.slice(0, 100);
      const chunks = [];
      for (let i = 0; i < partyIdsToProcess.length; i += CHUNK_SIZE) {
        chunks.push(partyIdsToProcess.slice(i, i + CHUNK_SIZE));
      }

      const allReceipts = [];

      // Process chunks sequentially
      for (const chunk of chunks) {
        const bulkResponse = await api.post('/ledger/bulk-receipt-data',
          { partyIds: chunk },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const {
          parties,
          transactionsMap,
          sales: allSales,
          bills: allBills,
          recoveries: allRecoveries,
          customerSales: allCustomerSales
        } = bulkResponse.data;

        for (const party of parties) {
          const transactions = transactionsMap[party._id] || [];
          const balance = party.currentBalance || 0;
          const isReceivable = balance > 0;

          let latestTransaction = null;
          let previousBalance = 0;
          let newBillAmount = 0;
          let billNumberText = null;
          let amountPaid = 0;

          const isCustomer = party.type === 'customer';
          let targetDate = selectedDate || getTodayDate();
          const relevantTransactions = transactions
            .filter(tx => {
              const isRelevantType = isCustomer ? (tx.saleId && tx.type === 'debit') : (tx.billId && tx.type === 'debit');
              if (!isRelevantType) return false;

              const txDate = toLocalISOString(tx.date);
              return txDate === targetDate;
            })
            .sort((a, b) => new Date(b.date) - new Date(a.date));

          let customerDailySales = [];
          if (isCustomer) {
            const customerSales = allCustomerSales.filter(sale => {
              const sellerName = sale.sellerNameUrdu || sale.sellerName || '';
              const sellerNameEn = sale.sellerName || '';
              const partyName = party.nameUrdu || party.name || '';
              const partyNameEn = party.name || '';

              return (sellerName && sellerName === partyName) ||
                (sellerNameEn && sellerNameEn === partyNameEn) ||
                (sale.customerPartyId && (
                  (typeof sale.customerPartyId === 'object' && sale.customerPartyId._id === party._id) ||
                  (typeof sale.customerPartyId === 'string' && sale.customerPartyId === party._id)
                ));
            });

            // Use the same target date as for transactions
            let targetDateInner = targetDate;

            customerDailySales = customerSales.filter(sale => {
              if (!sale.date) return false;
              const saleDate = toLocalISOString(sale.date);
              return saleDate === targetDateInner;
            });
          }

          if (relevantTransactions.length > 0) {
            latestTransaction = relevantTransactions[0];

            if (isCustomer && customerDailySales.length > 0) {
              const lastDaySalesTotal = customerDailySales.reduce((sum, sale) => {
                return sum + (parseFloat(sale.totalAmount) || parseFloat(sale.total) || 0);
              }, 0);

              previousBalance = Math.abs(balance) - lastDaySalesTotal;
              if (previousBalance < 0) previousBalance = 0;
              newBillAmount = lastDaySalesTotal;
            }

            if (isCustomer && latestTransaction.saleId) {
              const saleIdStr = typeof latestTransaction.saleId === 'object' ? latestTransaction.saleId._id : latestTransaction.saleId;
              const sale = allSales.find(s => s._id === saleIdStr);
              if (sale && sale.serialNumber) {
                billNumberText = companyLanguage === 'ur' ? `سیل # ${sale.serialNumber}` : `Sale # ${sale.serialNumber}`;
              } else {
                billNumberText = companyLanguage === 'ur' ? `سیل # ${saleIdStr.toString().slice(-6)}` : `Sale # ${saleIdStr.toString().slice(-6)}`;
              }

              const creditTransactions = transactions
                .filter(tx => tx.type === 'credit' && tx.recoveryId)
                .sort((a, b) => new Date(b.date) - new Date(a.date));

              if (creditTransactions.length > 0) {
                amountPaid = creditTransactions.reduce((sum, tx) => sum + (parseFloat(tx.amount) || 0), 0);

                if (creditTransactions[0].recoveryId) {
                  const recoveryIdStr = typeof creditTransactions[0].recoveryId === 'object' ? creditTransactions[0].recoveryId._id : creditTransactions[0].recoveryId;
                  const recovery = allRecoveries.find(r => r._id === recoveryIdStr);
                  if (recovery && recovery.recoveryAmount) {
                    amountPaid = parseFloat(recovery.recoveryAmount || 0);
                  }
                }
              }
            } else if (!isCustomer && latestTransaction.billId) {
              const billIdStr = typeof latestTransaction.billId === 'object' ? latestTransaction.billId._id : latestTransaction.billId;
              const bill = allBills.find(b => b._id === billIdStr);
              if (bill && bill.serialNumber) {
                billNumberText = companyLanguage === 'ur' ? `بل # ${bill.serialNumber}` : `Bill # ${bill.serialNumber}`;
              } else {
                billNumberText = companyLanguage === 'ur' ? `بل # ${billIdStr.toString().slice(-6)}` : `Bill # ${billIdStr.toString().slice(-6)}`;
              }
            }

            if (!isCustomer || customerDailySales.length === 0) {
              newBillAmount = latestTransaction.amount || 0;
              previousBalance = Math.abs(balance) - newBillAmount;
              if (previousBalance < 0) previousBalance = 0;
            }
          } else {
            previousBalance = Math.abs(balance);
          }

          allReceipts.push({
            party: party,
            balance: Math.abs(balance),
            isReceivable: isReceivable,
            transactions: transactions,
            date: new Date().toLocaleDateString('en-CA'),
            company: company,
            latestTransaction: latestTransaction,
            billNumber: billNumberText,
            billDate: latestTransaction ? latestTransaction.date : null,
            previousBalance: previousBalance,
            newBillAmount: newBillAmount,
            amountPaid: amountPaid,
            totalPayable: Math.abs(balance),
            customerDailySales: customerDailySales
          });
        }

        // Update state incrementally
        setAllReceiptsData([...allReceipts]);
      }

      return allReceipts;
    } catch (error) {
      console.error('Generate receipts data error:', error);
      return [];
    }
  };

  // Ref to prevent concurrent generation calls
  const isGeneratingRef = useRef(false);

  // Effect to generate all receipts data when in 'receipt' view mode
  useEffect(() => {
    // Only start if in receipt mode, has parties, and NOT already generating
    if (viewMode === 'receipt' && filteredParties.length > 0 && !isGeneratingRef.current) {
      const fetchAllReceipts = async () => {
        try {
          isGeneratingRef.current = true;
          setIsGeneratingAll(true);

          await generateAllReceiptsData();
        } catch (error) {
          console.error("Error generating all receipts:", error);
        } finally {
          setIsGeneratingAll(false);
          isGeneratingRef.current = false;
        }
      };
      fetchAllReceipts();
    }
  }, [viewMode, filteredParties, selectedDate, showAllParties, activeTab]);

  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = isSomeSelected && !isAllSelected;
    }
  }, [isSomeSelected, isAllSelected]);

  const handlePrintSelected = async () => {
    if (selectedParties.length === 0) {
      toast.warning(companyLanguage === 'ur' ? 'براہ کرم کم از کم ایک رسید منتخب کریں' : 'Please select at least one receipt');
      return;
    }

    try {
      setIsGeneratingSelected(true);
      const token = getToken();
      if (!token) {
        setIsGeneratingSelected(false);
        return;
      }

      // Optimization: Use specialized bulk endpoint instead of multiple individual calls
      const bulkResponse = await api.post('/ledger/bulk-receipt-data',
        { partyIds: selectedParties },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const {
        parties,
        transactionsMap,
        sales: allSales,
        bills: allBills,
        recoveries: allRecoveries,
        customerSales: allCustomerSales
      } = bulkResponse.data;

      const receipts = [];

      for (const partyId of selectedParties) {
        const party = parties.find(p => p._id === partyId);
        if (!party) continue;

        const transactions = transactionsMap[partyId] || [];
        const balance = party.currentBalance || 0;
        const isReceivable = balance > 0;

        let latestTransaction = null;
        let previousBalance = 0;
        let newBillAmount = 0;
        let billNumberText = null;
        let amountPaid = 0;

        const isCustomer = party.type === 'customer';
        let targetDate = selectedDate || null;
        const relevantTransactions = transactions
          .filter(tx => {
            const isRelevantType = isCustomer ? (tx.saleId && tx.type === 'debit') : (tx.billId && tx.type === 'debit');
            if (!isRelevantType) return false;

            if (targetDate) {
              const txDate = new Date(tx.date).toLocaleDateString('en-CA');
              return txDate === targetDate;
            }
            return true;
          })
          .sort((a, b) => new Date(b.date) - new Date(a.date));

        let customerDailySales = [];
        if (isCustomer) {
          // Optimization: Use pre-fetched allCustomerSales (already filtered on backend)
          const customerSales = allCustomerSales.filter(sale => {
            const sellerName = sale.sellerNameUrdu || sale.sellerName || '';
            const sellerNameEn = sale.sellerName || '';
            const partyName = party.nameUrdu || party.name || '';
            const partyNameEn = party.name || '';

            return (sellerName && sellerName === partyName) ||
              (sellerNameEn && sellerNameEn === partyNameEn) ||
              (sale.customerPartyId && (
                (typeof sale.customerPartyId === 'object' && sale.customerPartyId._id === party._id) ||
                (typeof sale.customerPartyId === 'string' && sale.customerPartyId === party._id)
              ));
          });

          // Optimization: Use selectedDate if available
          let targetDate = selectedDate || null;

          if (!targetDate && customerSales.length > 0) {
            const sortedSales = customerSales
              .filter(sale => sale.date)
              .sort((a, b) => new Date(b.date) - new Date(a.date));

            if (sortedSales.length > 0) {
              targetDate = toLocalISOString(sortedSales[0].date);
            }
          }

          if (!targetDate) {
            if (relevantTransactions.length > 0) {
              targetDate = toLocalISOString(relevantTransactions[0].date);
            } else {
              targetDate = getTodayDate();
            }
          }

          customerDailySales = customerSales.filter(sale => {
            if (!sale.date) return false;
            const saleDate = toLocalISOString(sale.date);
            return saleDate === targetDate;
          });
        }

        if (relevantTransactions.length > 0) {
          latestTransaction = relevantTransactions[0];

          if (isCustomer && customerDailySales.length > 0) {
            const lastDaySalesTotal = customerDailySales.reduce((sum, sale) => {
              return sum + (parseFloat(sale.totalAmount) || parseFloat(sale.total) || 0);
            }, 0);

            previousBalance = Math.abs(balance) - lastDaySalesTotal;
            if (previousBalance < 0) previousBalance = 0;
            newBillAmount = lastDaySalesTotal;
          }

          if (isCustomer && latestTransaction.saleId) {
            const saleIdStr = typeof latestTransaction.saleId === 'object' ? latestTransaction.saleId._id : latestTransaction.saleId;
            const sale = allSales.find(s => s._id === saleIdStr);

            if (sale && sale.serialNumber) {
              billNumberText = companyLanguage === 'ur' ? `سیل # ${sale.serialNumber}` : `Sale # ${sale.serialNumber}`;
            } else {
              billNumberText = companyLanguage === 'ur' ? `سیل # ${saleIdStr.toString().slice(-6)}` : `Sale # ${saleIdStr.toString().slice(-6)}`;
            }

            const creditTransactions = transactions
              .filter(tx => tx.type === 'credit' && tx.recoveryId)
              .sort((a, b) => new Date(b.date) - new Date(a.date));

            if (creditTransactions.length > 0) {
              amountPaid = creditTransactions.reduce((sum, tx) => sum + (parseFloat(tx.amount) || 0), 0);

              if (creditTransactions[0].recoveryId) {
                const recoveryIdStr = typeof creditTransactions[0].recoveryId === 'object' ? creditTransactions[0].recoveryId._id : creditTransactions[0].recoveryId;
                const recovery = allRecoveries.find(r => r._id === recoveryIdStr);
                if (recovery && recovery.recoveryAmount) {
                  amountPaid = parseFloat(recovery.recoveryAmount || 0);
                }
              }
            }
          } else if (!isCustomer && latestTransaction.billId) {
            const billIdStr = typeof latestTransaction.billId === 'object' ? latestTransaction.billId._id : latestTransaction.billId;
            const bill = allBills.find(b => b._id === billIdStr);
            if (bill && bill.serialNumber) {
              billNumberText = companyLanguage === 'ur' ? `بل # ${bill.serialNumber}` : `Bill # ${bill.serialNumber}`;
            } else {
              billNumberText = companyLanguage === 'ur' ? `بل # ${billIdStr.toString().slice(-6)}` : `Bill # ${billIdStr.toString().slice(-6)}`;
            }
          }

          if (!isCustomer || customerDailySales.length === 0) {
            newBillAmount = latestTransaction.amount || 0;
            previousBalance = Math.abs(balance) - newBillAmount;
            if (previousBalance < 0) previousBalance = 0;
          }
        } else {
          previousBalance = Math.abs(balance);
        }

        receipts.push({
          party: party,
          balance: Math.abs(balance),
          isReceivable: isReceivable,
          transactions: transactions,
          date: new Date().toLocaleDateString('en-CA'),
          company: company,
          latestTransaction: latestTransaction,
          billNumber: billNumberText,
          billDate: latestTransaction ? latestTransaction.date : null,
          previousBalance: previousBalance,
          newBillAmount: newBillAmount,
          amountPaid: amountPaid,
          totalPayable: Math.abs(balance),
          customerDailySales: customerDailySales
        });
      }

      setMultipleReceiptsData(receipts);
      setShowReceipt(true);
    } catch (error) {
      console.error('Print selected receipts error:', error);
      toast.error(companyLanguage === 'ur' ? 'رسیدیں بنانے میں ناکام' : 'Failed to generate receipts');
    } finally {
      setIsGeneratingSelected(false);
    }
  };



  // Excel export function
  const handleExportExcel = async () => {
    try {
      if (filteredParties.length === 0) {
        toast.warning(companyLanguage === 'ur' ? 'برائے مہربانی پہلے ڈیٹا فلٹر کریں' : 'Please filter data first');
        return;
      }

      toast.info(companyLanguage === 'ur' ? 'Excel فائل تیار ہو رہی ہے...' : 'Preparing Excel file...');

      // Generate all receipts data
      const receiptsData = await generateAllReceiptsData();

      if (receiptsData.length === 0) {
        toast.error(companyLanguage === 'ur' ? 'کوئی ڈیٹا نہیں ملا' : 'No data found');
        return;
      }

      const excelData = [];

      // Add header - use Sale Number for customers, Bill Number for suppliers
      const numberLabel = activeTab === 'customers'
        ? (isUrdu ? 'سیل نمبر' : 'Sale Number')
        : (isUrdu ? 'بل نمبر' : 'Bill Number');
      const dateLabel = activeTab === 'customers'
        ? (isUrdu ? 'سیل تاریخ' : 'Sale Date')
        : (isUrdu ? 'بل تاریخ' : 'Bill Date');

      excelData.push([
        isUrdu ? 'تاریخ' : 'Date',
        isUrdu ? 'نام' : 'Name',
        isUrdu ? 'فون' : 'Phone',
        isUrdu ? 'پتہ' : 'Address',
        numberLabel,
        dateLabel,
        isUrdu ? 'پچھلی بقیہ رقم' : 'Previous Balance',
        isUrdu ? 'نیا بل' : 'New Bill',
        isUrdu ? 'کل قابل ادائیگی' : 'Total Payable',
        isUrdu ? 'قسم' : 'Type'
      ]);

      // Add data rows
      receiptsData.forEach(receipt => {
        excelData.push([
          receipt.date || '-',
          isUrdu ? (receipt.party.nameUrdu || receipt.party.name) : receipt.party.name,
          receipt.party.phone || '-',
          receipt.party.address || '-',
          receipt.billNumber || '-',
          receipt.billDate ? new Date(receipt.billDate).toLocaleDateString() : '-',
          receipt.previousBalance || 0,
          receipt.newBillAmount || 0,
          receipt.totalPayable || 0,
          receipt.isReceivable ? (isUrdu ? 'قابل وصول' : 'Receivable') : (isUrdu ? 'قابل ادائیگی' : 'Payable')
        ]);
      });

      // Add total row
      const totalPreviousBalance = receiptsData.reduce((sum, r) => sum + (r.previousBalance || 0), 0);
      const totalNewBill = receiptsData.reduce((sum, r) => sum + (r.newBillAmount || 0), 0);
      const totalPayable = receiptsData.reduce((sum, r) => sum + (r.totalPayable || 0), 0);

      excelData.push([
        isUrdu ? 'کل' : 'Total',
        '',
        '',
        '',
        '',
        '',
        totalPreviousBalance,
        totalNewBill,
        totalPayable,
        ''
      ]);

      // Create workbook and worksheet
      const ws = XLSX.utils.aoa_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, isUrdu ? 'رسیدیں' : 'Receipts');

      // Generate filename
      const tabName = activeTab === 'suppliers' ? (isUrdu ? 'سپلائر' : 'Suppliers') : (isUrdu ? 'کسٹمر' : 'Customers');
      const fileName = `Receipts_${tabName}_${new Date().toLocaleDateString('en-CA')}.xlsx`;

      XLSX.writeFile(wb, fileName);
      toast.success(companyLanguage === 'ur' ? 'Excel فائل ڈاؤن لوڈ ہو گئی' : 'Excel file downloaded successfully');
    } catch (error) {
      console.error('Excel download error:', error);
      toast.error(companyLanguage === 'ur' ? 'Excel ڈاؤن لوڈ میں ناکام' : 'Failed to download Excel');
    }
  };

  const isUrdu = companyLanguage === 'ur';

  if (showReceipt && (receiptData || multipleReceiptsData.length > 0)) {
    return (
      <ReceiptView
        receiptData={receiptData}
        multipleReceiptsData={multipleReceiptsData}
        companyDataProp={company}
        onClose={handleCloseReceipt}
        onPrint={handlePrintReceipt}
      />
    );
  }

  return (
    <>
      <Helmet>
        <title>{isUrdu ? 'رسید مینجمنٹ' : 'Receipt Management'} - Bexon</title>
        <style>{`
          @media print {
            @page {
              margin: 0.5cm;
            }
            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .print\\:hidden {
              display: none !important;
            }
            .print\\:block {
              display: block !important;
            }
            .print\\:border-b {
              border-bottom: 1px solid #e5e7eb !important;
            }
            .print\\:px-2 {
              padding-left: 0.5rem !important;
              padding-right: 0.5rem !important;
            }
            .print\\:py-2 {
              padding-top: 0.5rem !important;
              padding-bottom: 0.5rem !important;
            }
            .print\\:text-sm {
              font-size: 0.875rem !important;
            }
            .print\\:text-2xl {
              font-size: 1.5rem !important;
            }
            .print\\:mb-2 {
              margin-bottom: 0.5rem !important;
            }
            .print\\:p-0 {
              padding: 0 !important;
            }
            .print\\:bg-white {
              background-color: white !important;
            }
            .print\\:shadow-none {
              box-shadow: none !important;
            }
            .print\\:border-none {
              border: none !important;
            }
            .print\\:grid-cols-5 {
              grid-template-columns: repeat(5, minmax(0, 1fr)) !important;
            }
            .print\\:col-span-2 {
              grid-column: span 2 / span 2 !important;
            }
            .print\\:col-span-1 {
              grid-column: span 1 / span 1 !important;
            }
          }
        `}</style>
      </Helmet>

      <div className={`min-h-screen bg-brand-background p-6 print:p-0 print:bg-white ${isUrdu ? 'font-urdu' : ''}`} dir={isUrdu ? 'rtl' : 'ltr'}>
        {/* Print Header - Company Info - Centered for list print */}
        {company && (
          <div className="hidden print:block border-b-2 border-gray-400 pb-3 mb-4 bg-white p-4">
            {/* Company Name and Date - Flex container for Print */}
            <div className="flex justify-between items-start mb-2 mt-4">
              <div className="flex-1 text-center">
                <h1
                  className={`text-xl font-bold text-brand-header ${isUrdu ? 'font-urdu' : ''}`}
                  style={{
                    ...(isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}),
                    color: '#1e40af',
                    WebkitPrintColorAdjust: 'exact',
                    printColorAdjust: 'exact',
                    visibility: 'visible',
                    opacity: 1
                  }}
                >
                  {isUrdu ? (company?.shopNameUrdu || company?.shopName || company?.username) : (company?.shopName || company?.username || company?.shopNameUrdu || 'Company Name')}
                </h1>
                {/* Name field hidden in print view */}
                {company?.username && (
                  <p className={`text-sm text-gray-600 font-medium mt-1 print:hidden ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                    {isUrdu ? 'نام:' : 'Name:'} {company.username}
                  </p>
                )}
              </div>

              {/* Print Date */}
              <div className="text-right whitespace-nowrap pt-2">
                <p className="text-sm font-semibold text-gray-700">
                  {isUrdu ? 'تاریخ:' : 'Date:'} {new Date().toLocaleDateString('en-GB')}
                </p>
              </div>
            </div>

            {/* Phone Numbers - Single Row */}
            {(() => {
              const phoneNumbers = [];

              // Collect all phone numbers from contacts
              if (company?.contacts && company.contacts.length > 0) {
                company.contacts.forEach((contact) => {
                  if (contact.number && contact.number.trim()) {
                    phoneNumbers.push({
                      name: contact.name,
                      number: contact.number
                    });
                  }
                });
              }

              // Collect all phone numbers from persons
              if (company?.persons && company.persons.length > 0) {
                company.persons.forEach((person) => {
                  if (person.contactNumber && person.contactNumber.trim()) {
                    phoneNumbers.push({
                      name: person.name,
                      number: person.contactNumber
                    });
                  }
                });
              }

              // Reverse the order: second becomes first, first becomes second
              const reversedPhoneNumbers = phoneNumbers.reverse();

              return reversedPhoneNumbers.length > 0 ? (
                <div className="flex justify-between items-center mt-3 gap-8">
                  {reversedPhoneNumbers.length > 0 && (
                    <span className="text-xs text-gray-700 font-semibold text-left">
                      {reversedPhoneNumbers[0].name ? `${reversedPhoneNumbers[0].name}: ` : ''}{reversedPhoneNumbers[0].number}
                    </span>
                  )}
                  {reversedPhoneNumbers.length > 1 && (
                    <span className="text-xs text-gray-700 font-semibold text-right">
                      {reversedPhoneNumbers[1].name ? `${reversedPhoneNumbers[1].name}: ` : ''}{reversedPhoneNumbers[1].number}
                    </span>
                  )}
                </div>
              ) : null;
            })()}

            {/* Address - Centered */}
            {company?.address && (
              <p className={`text-xs text-gray-600 mt-2 text-center ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                {company.address}
              </p>
            )}
          </div>
        )}

        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6 border border-brand-card print:shadow-none print:border-none print:mb-2 print:hidden">
            <h1 className={`text-3xl font-bold text-brand-header mb-4 print:text-2xl ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
              {isUrdu ? 'رسید مینجمنٹ' : 'Receipt Management'}
            </h1>
            <p className={`text-gray-600 print:hidden ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
              {isUrdu ? 'گاہکوں اور سپلائرز کے لیے رسید بنائیں اور پرنٹ کریں' : 'Generate and print receipts for customers and suppliers'}
            </p>
          </div>

          {/* Tabs + Search Filter */}
          <div className="bg-white rounded-lg shadow-lg p-4 mb-6 border border-brand-card print:shadow-none print:border-none print:mb-2 print:p-2">
            {/* Main Tabs Group */}
            <div className={`flex flex-col md:flex-row gap-4 mb-6 print:hidden ${isUrdu ? 'md:flex-row-reverse' : ''}`}>
              {/* Party Type Tabs */}
              <div className="inline-flex rounded-lg overflow-hidden border border-gray-200 bg-gray-50 h-10">
                <button
                  type="button"
                  onClick={() => setActiveTab('suppliers')}
                  className={`px-4 py-2 text-sm font-semibold transition-all ${activeTab === 'suppliers' ? 'bg-brand-header text-white' : 'text-gray-700 hover:bg-gray-100'} ${isUrdu ? 'font-urdu' : ''}`}
                >
                  {isUrdu ? 'سپلائر رسیدیں' : 'Supplier Receipts'}
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('customers')}
                  className={`px-4 py-2 text-sm font-semibold transition-all ${activeTab === 'customers' ? 'bg-brand-header text-white' : 'text-gray-700 hover:bg-gray-100'} ${isUrdu ? 'font-urdu' : ''}`}
                >
                  {isUrdu ? 'کسٹمر رسیدیں' : 'Customer Receipts'}
                </button>
              </div>

              {/* View Mode Tabs */}
              <div className="inline-flex rounded-lg overflow-hidden border border-gray-200 bg-gray-50 h-10">
                <button
                  type="button"
                  onClick={() => setViewMode('sale-wise')}
                  className={`px-4 py-2 text-sm font-semibold transition-all ${viewMode === 'sale-wise' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'} ${isUrdu ? 'font-urdu' : ''}`}
                >
                  {isUrdu ? 'سیل وائز' : 'Sale Wise'}
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('receipt')}
                  className={`px-4 py-2 text-sm font-semibold transition-all ${viewMode === 'receipt' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'} ${isUrdu ? 'font-urdu' : ''}`}
                >
                  {isUrdu ? 'رسید' : 'Receipt'}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-4 print:flex-col print:items-start print:gap-2">
              <div className="flex-1 print:w-full">
                <input
                  type="text"
                  placeholder={isUrdu ? 'نام سے تلاش کریں...' : 'Search by name...'}
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent print:hidden"
                  style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif", direction: 'rtl' } : {}}
                />
              </div>
              <div className="print:hidden">
                <label className={`block text-sm font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {isUrdu ? 'تاریخ فلٹر:' : 'Date Filter:'}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent"
                    style={isUrdu ? { direction: 'ltr' } : {}}
                  />
                  <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
                    <input
                      type="checkbox"
                      checked={showAllParties}
                      onChange={(e) => setShowAllParties(e.target.checked)}
                      className="w-4 h-4 text-brand-header border-gray-300 rounded focus:ring-brand-header"
                    />
                    <span className={`text-xs font-semibold text-gray-600 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                      {isUrdu ? 'تمام کسٹمرز دکھائیں' : 'Show All'}
                    </span>
                  </label>
                </div>
                {selectedDate && (
                  <button
                    onClick={() => setSelectedDate('')}
                    className="ml-2 px-3 py-2 text-sm bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-all"
                    title={isUrdu ? 'فلٹر صاف کریں' : 'Clear filter'}
                  >
                    {isUrdu ? '×' : '×'}
                  </button>
                )}
                {isLoadingDateFilter && (
                  <div className="ml-2 flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-header"></div>
                    <span className={`text-sm text-gray-500 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                      {isUrdu ? 'لوڈ ہو رہا ہے...' : 'Loading...'}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-4 print:hidden">
                <div className="text-gray-600 text-sm sm:text-base">
                  {isUrdu ? 'کل پارٹیز:' : 'Total Parties:'}{' '}
                  <span className="font-bold text-brand-header">{filteredParties.length}</span>
                  <span className="ml-2 text-xs text-gray-500">
                    {isUrdu
                      ? activeTab === 'suppliers'
                        ? '(سپلائر)'
                        : '(کسٹمر)'
                      : activeTab === 'suppliers'
                        ? '(Suppliers)'
                        : '(Customers)'}
                  </span>
                </div>
                {selectedParties.length > 0 && (
                  <div className="text-gray-600">
                    {isUrdu ? 'منتخب:' : 'Selected:'} <span className="font-bold text-brand-header">{selectedParties.length}</span>
                    {selectedParties.length > 0 && (
                      <span className="ml-2 text-xs text-gray-500">
                        ({isUrdu ? `${Math.ceil(selectedParties.length / 4)} صفحات` : `${Math.ceil(selectedParties.length / 4)} pages`})
                      </span>
                    )}
                  </div>
                )}
                {selectedParties.length > 0 && (
                  <button
                    onClick={handlePrintSelected}
                    disabled={isGeneratingSelected}
                    className="bg-brand-header hover:bg-emerald-800 text-white font-semibold px-6 py-2 rounded-lg transition-all transform hover:scale-105 hover:shadow-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isGeneratingSelected ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {isUrdu ? 'تیار ہو رہا ہے...' : 'Generating...'}
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                        </svg>
                        {isUrdu ? `${selectedParties.length} رسیدیں پرنٹ کریں` : `Print ${selectedParties.length} Receipts`}
                      </>
                    )}
                  </button>
                )}
                {filteredParties.length > 0 && (
                  <>
                    <button
                      onClick={handlePrintList}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-lg transition-all transform hover:scale-105 hover:shadow-lg flex items-center gap-2 print:hidden"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      {isUrdu ? 'پرنٹ کریں' : 'Print'}
                    </button>
                    <button
                      onClick={handleExportExcel}
                      className="bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-2 rounded-lg transition-all transform hover:scale-105 hover:shadow-lg flex items-center gap-2 print:hidden"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {isUrdu ? 'Excel میں ڈاؤن لوڈ کریں' : 'Download Excel'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Parties List Table (Used for both modes) */}
          {isLoading ? (
            <div className="bg-white rounded-lg shadow-lg p-12 text-center border border-brand-card">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-header mx-auto mb-4"></div>
              <p className={`text-gray-600 ${isUrdu ? 'font-urdu' : ''}`}>
                {isUrdu ? 'لوڈ ہو رہا ہے...' : 'Loading parties...'}
              </p>
            </div>
          ) : filteredParties.length === 0 ? (
            <div className="bg-white rounded-lg shadow-lg p-12 text-center border border-brand-card">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className={`text-gray-600 text-lg ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                {isUrdu ? 'کوئی ڈیٹا نہیں ملا' : 'No data found'}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              {/* Receipt Tab Batch Controls */}
              {viewMode === 'receipt' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex flex-col md:flex-row justify-between items-center print:hidden shadow-sm">
                  <div className="flex items-center gap-3 mb-3 md:mb-0">
                    <div className="bg-blue-100 p-2 rounded-full">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className={`text-lg font-bold text-blue-900 ${isUrdu ? 'font-urdu' : ''}`}>
                        {isUrdu ? 'تمام رسیدیں' : 'Batch Receipts'}
                      </h3>
                      <p className="text-sm text-blue-600">
                        {isUrdu ? `${filteredParties.length} گاہکوں کے لیے رسیدیں تیار کریں` : `Generate receipts for all ${filteredParties.length} customers`}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    {allReceiptsData.length > 0 ? (
                      <button
                        onClick={() => window.print()}
                        className="bg-brand-header hover:bg-emerald-800 text-white font-semibold px-6 py-2 rounded-lg transition-all flex items-center gap-2 shadow-md"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                        </svg>
                        {isUrdu ? 'سب پرنٹ کریں' : 'Print All'}
                      </button>
                    ) : (
                      <button
                        onClick={generateAllReceiptsData}
                        disabled={isGeneratingAll}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-lg transition-all flex items-center gap-2 shadow-md disabled:opacity-50"
                      >
                        {isGeneratingAll ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        )}
                        {isUrdu ? 'سب تیار کریں' : 'Load All Receipts'}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Batch Print Preview Area (Hidden on screen unless scrolling) */}
              {allReceiptsData.length > 0 && viewMode === 'receipt' && (
                <div className="sr-only print:not-sr-only print:block">
                  <ReceiptView
                    multipleReceiptsData={allReceiptsData}
                    isInline={true}
                    companyDataProp={company}
                    onClose={() => setAllReceiptsData([])}
                  />
                </div>
              )}

              <div className="bg-white rounded-lg shadow-lg border border-brand-card overflow-hidden print:shadow-none print:border-none">
                {/* Table Header */}
                <div className={`bg-gray-50 border-b border-gray-200 px-6 py-4 print:px-0 print:py-0 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  <div className="grid grid-cols-12 gap-2 items-center print:grid-cols-6 print:gap-0 print:border-y print:border-gray-400">
                    <div className="col-span-1 print:hidden">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          ref={selectAllCheckboxRef}
                          checked={isAllSelected}
                          onChange={handleSelectAll}
                          className="w-5 h-5 text-brand-header border-gray-300 rounded focus:ring-brand-header focus:ring-2 cursor-pointer"
                        />
                        <span className={`ml-2 text-sm font-semibold text-gray-700 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                          {isUrdu ? 'سب منتخب' : 'Select All'}
                        </span>
                      </label>
                    </div>
                    <div className="col-span-1 print:col-span-1 text-center print:border-x print:border-gray-400 print:py-1">
                      <span className="text-sm font-semibold text-gray-700 print:text-xs">{isUrdu ? 'نمبر' : 'Sr. No.'}</span>
                    </div>
                    <div className={`col-span-4 print:col-span-4 print:border-x print:border-gray-400 print:py-1 ${isUrdu ? 'text-right px-2' : 'text-left px-2'}`}>
                      <span className="text-sm font-semibold text-gray-700 print:text-xs">{isUrdu ? 'نام' : 'Name'}</span>
                    </div>
                    <div className="col-span-2 print:hidden text-right">
                      <span className="text-sm font-semibold text-gray-700">{isUrdu ? 'آج کی سیل' : "Today's Sale"}</span>
                    </div>
                    <div className="col-span-2 print:col-span-1 text-right print:border-x print:border-gray-400 print:py-1 px-2">
                      <span className="text-sm font-semibold text-gray-700 print:text-xs">{isUrdu ? 'کل بیلنس' : 'Total Balance'}</span>
                    </div>
                    <div className="col-span-1 print:hidden text-center">
                      <span className="text-sm font-semibold text-gray-700">{isUrdu ? 'قسم' : 'Type'}</span>
                    </div>
                    <div className="col-span-1 print:hidden">
                      <span className="text-sm font-semibold text-gray-700">{isUrdu ? 'عمل' : 'Action'}</span>
                    </div>
                  </div>
                </div>

                {/* Table Body */}
                <div className="divide-y divide-gray-200">
                  {filteredParties.map((party, index) => {
                    const balance = party.currentBalance || 0;
                    const isReceivable = balance > 0;
                    const balanceAmount = Math.abs(balance);
                    const isSelected = selectedParties.includes(party._id);
                    const serialNumber = index + 1;

                    return (
                      <div
                        key={party._id}
                        className={`px-6 py-4 hover:bg-gray-50 transition-colors print:px-2 print:py-0.5 print:border-b print:border-gray-200 ${isSelected ? 'bg-emerald-50 border-l-4 border-brand-header print:border-l-0' : ''
                          }`}
                      >
                        <div className="grid grid-cols-12 gap-2 items-center print:grid-cols-6 print:gap-0">
                          {/* Checkbox */}
                          <div className="col-span-1 print:hidden">
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleToggleSelection(party._id)}
                                className="w-5 h-5 text-brand-header border-gray-300 rounded focus:ring-brand-header focus:ring-2 cursor-pointer"
                              />
                            </label>
                          </div>

                          {/* Serial Number */}
                          <div className="col-span-1 print:col-span-1 text-center print:border-x print:border-gray-400 print:py-0.5">
                            <span className="text-sm font-semibold text-gray-700 print:text-[10px]">
                              {serialNumber}
                            </span>
                          </div>

                          {/* Name */}
                          <div className={`col-span-4 print:col-span-4 print:border-x print:border-gray-400 print:py-0.5 ${isUrdu ? 'text-right px-2' : 'text-left px-2'}`}>
                            <h3 className={`text-base font-bold text-gray-800 print:text-[10px] ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                              {isUrdu ? (party.nameUrdu || party.name) : party.name}
                            </h3>
                          </div>

                          {/* Today's Sale hidden in print */}
                          <div className="col-span-2 print:hidden text-right">
                            <span className={`text-lg font-bold ${partyDateSalesTotals[party._id] > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                              Rs. {(partyDateSalesTotals[party._id] || 0).toLocaleString()}
                            </span>
                          </div>

                          {/* Balance */}
                          <div className="col-span-2 print:col-span-1 text-right print:border-x print:border-gray-400 print:py-0.5 px-2">
                            <span className={`text-lg font-bold print:text-[10px] ${isReceivable ? 'text-emerald-600' : 'text-red-600'}`}>
                              Rs. {balanceAmount.toLocaleString()}
                            </span>
                          </div>

                          {/* Type Badge - Screen view only */}
                          <div className="col-span-1 print:hidden text-center">
                            <div className={`px-2 py-1 rounded-full text-xs font-semibold inline-block ${isReceivable
                              ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                              : 'bg-red-100 text-red-700 border border-red-200'
                              }`}>
                              {isReceivable ? (isUrdu ? 'قابل وصول' : 'Receivable') : (isUrdu ? 'قابل ادائیگی' : 'Payable')}
                            </div>
                          </div>

                          {/* Action Button - Screen view only */}
                          <div className="col-span-1 print:hidden">
                            <button
                              onClick={() => handleGenerateReceipt(party)}
                              disabled={generatingReceiptId === party._id}
                              className="bg-brand-header hover:bg-emerald-800 text-white font-semibold px-4 py-2 rounded-lg transition-all text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {generatingReceiptId === party._id ? (
                                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                </svg>
                              )}
                              {generatingReceiptId === party._id
                                ? (isUrdu ? 'تیار ہو رہا ہے...' : 'Generating...')
                                : (isUrdu ? 'رسید' : 'Receipt')}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Total Row */}
                  {filteredParties.length > 0 && (() => {
                    const totalBalance = filteredParties.reduce((sum, party) => {
                      const balance = party.currentBalance || 0;
                      return sum + Math.abs(balance);
                    }, 0);

                    return (
                      <div className="px-6 py-4 bg-gradient-to-r from-emerald-50 to-green-50 border-t-4 border-emerald-500 font-bold print:px-0 print:py-0 print:border-t-2">
                        <div className="grid grid-cols-12 gap-2 items-center print:grid-cols-6 print:gap-0 font-bold border-t-2 border-gray-400">
                          {/* Empty space for checkbox */}
                          <div className="col-span-1 print:hidden"></div>

                          {/* Empty space for serial number */}
                          <div className="col-span-1 print:col-span-1 print:border-x print:border-gray-400 print:py-1"></div>

                          {/* Total Label */}
                          <div className={`col-span-4 print:col-span-4 print:border-x print:border-gray-400 print:py-1 ${isUrdu ? 'text-right px-2' : 'text-left px-2'}`}>
                            <span className={`text-base font-bold text-gray-800 print:text-xs ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                              {isUrdu ? 'کل بیلنس:' : 'Total Balance:'}
                            </span>
                          </div>

                          {/* Empty for Today's Sale total - hidden in print */}
                          <div className="col-span-2 print:hidden text-right"></div>

                          {/* Total Amount */}
                          <div className="col-span-2 print:col-span-1 text-right print:border-x print:border-gray-400 print:py-1 px-2">
                            <span className="text-xl font-bold print:text-[10px] text-emerald-700">
                              Rs. {totalBalance.toLocaleString()}
                            </span>
                          </div>

                          {/* Empty space for type badge */}
                          <div className="col-span-1 print:hidden"></div>

                          {/* Empty space for action button */}
                          <div className="col-span-1 print:hidden"></div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Single Receipt Modal */}
        {showReceipt && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-hidden flex flex-col relative animate-in fade-in zoom-in duration-300">
              <button
                onClick={() => setShowReceipt(false)}
                className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors z-[10000] group"
                title="Close"
              >
                <svg className="w-6 h-6 text-gray-400 group-hover:text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="flex-1 overflow-y-auto p-6 md:p-10 custom-scrollbar">
                <ReceiptView
                  receipt={receiptData}
                  party={selectedParty}
                  companyDataProp={company}
                  onClose={() => setShowReceipt(false)}
                />
              </div>

              <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t">
                <button
                  onClick={() => setShowReceipt(false)}
                  className="px-6 py-2 rounded-xl text-gray-600 hover:bg-gray-200 transition-all font-semibold"
                >
                  {isUrdu ? 'بند کریں' : 'Close'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default ReceiptManagement;

